import {
    INavLink,
    INavLinkGroup,
    INavStyles,
    IconButton,
    Nav,
    Panel,
    PanelType,
} from '@fluentui/react';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { getThemeFromString } from '../../../../Common/util/localStorageUtils';
import { AuthContext } from '../../../models/AuthContext';
import { ThemeContext } from '../../../models/ThemeContext';
import { getNavRoute, routeKeys } from '../../../../Common/util/RouteUtils';
import { CapabilityContext } from '../../../../Common/components/Capabilities/CapabilityContext';
import { Capability } from '../../../../Common/components/Capabilities/capability';
import { isKustoAccessConfiguredForCloud } from '../../../../ABSM/components/KustoAccess/kustoAccessConfig';

interface IMobilePageNavProps {
    isOpen: boolean;
    dismissPanel:
        | ((
              ev?:
                  | React.SyntheticEvent<HTMLElement, Event>
                  | KeyboardEvent
                  | undefined
          ) => void)
        | undefined;
}

export const MobilePageNav: React.FC<IMobilePageNavProps> = (
    props: IMobilePageNavProps
) => {
    const location = useLocation();
    const { authContext } = useContext(AuthContext);
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);

    const [selectedRouteKey, setSelectedRouteKey] = useState<
        string | undefined
    >(getNavRoute(location.pathname));

    useEffect(() => {
        setSelectedRouteKey(getNavRoute(location.pathname));
        if (props && props.dismissPanel) {
            props.dismissPanel();
        }
    }, [location]);

    // Use capabilities context
    const capabilities = useContext(CapabilityContext);
    // Only show Admin if user has Bridge.Admin role
    const showAdmin = capabilities.check(Capability.srmAdminRole);
    const inPublicCloud = capabilities.check(Capability.public);
    // Kusto access is still being validated in the sovereign and air-gapped clouds, so the
    // customer page is admin-only there, and only once that cloud has a cluster configured.
    const showKustoAccess =
        inPublicCloud || (showAdmin && isKustoAccessConfiguredForCloud());

    const navStyles: Partial<INavStyles> = {
        chevronIcon: {
            display: 'none',
        },
        chevronButton: {
            display: 'none',
        },
    };

    const [links, setLinks] = useState<INavLink[]>([
        {
            name: 'Home',
            url: routeKeys.Home,
            ariaLabel: 'Home',
            icon: 'HomeSolid',
            isExpanded: true,
            key: routeKeys.Home,
        },
    ]);

    useEffect(() => {
        let newLinks = [...links];
        if (authContext.isUserAuthorized) {
            let featureSubLinks: INavLink[] = [
                {
                    name: 'Releases',
                    url: routeKeys.Features.Releases.Latest,
                    ariaLabel: 'Releases',
                    icon: 'Rocket',
                    isExpanded: true,
                    key: routeKeys.Features.Releases.Base,
                },
                {
                    name: 'Builds',
                    url: routeKeys.Features.Builds.Latest,
                    ariaLabel: 'Builds',
                    icon: 'AllApps',
                    isExpanded: true,
                    key: routeKeys.Features.Builds.Base,
                },
            ];

            newLinks.push({
                name: 'Features',
                url: routeKeys.Features.Releases.Latest,
                ariaLabel: 'Features',
                icon: 'Product',
                isExpanded: true,
                key: routeKeys.Features.Base,
                links: featureSubLinks,
            });

            if (showKustoAccess) {
                newLinks.push({
                    name: 'Onboarding',
                    url: routeKeys.Onboarding.Kusto.Base,
                    ariaLabel: 'Onboarding',
                    icon: 'FollowUser',
                    isExpanded: true,
                    key: routeKeys.Onboarding.Base,
                    links: [
                        {
                            name: 'Kusto',
                            url: routeKeys.Onboarding.Kusto.Base,
                            ariaLabel: 'Kusto access',
                            icon: 'Database',
                            isExpanded: true,
                            key: routeKeys.Onboarding.Kusto.Base,
                        },
                    ],
                });
            }
        }

        if (showAdmin) {
            newLinks.push(
                {
                    name: 'Separator',
                    url: '',
                },
                {
                    name: 'Administration',
                    url: routeKeys.Administration,
                    ariaLabel: 'Administration',
                    icon: 'Settings',
                    isExpanded: true,
                    key: routeKeys.Administration,
                    links: [
                        {
                            name: 'Cloud Status',
                            url: routeKeys.CloudStatus,
                            ariaLabel: 'Cloud Status',
                            icon: 'Cloud',
                            key: routeKeys.CloudStatus,
                        },
                        {
                            name: 'Environment Data',
                            url: routeKeys.EnvironmentData,
                            ariaLabel: 'Environment Data',
                            icon: 'ServerEnviroment',
                            key: routeKeys.EnvironmentData,
                        },
                        {
                            name: 'DRI Tools',
                            url: routeKeys.DRITools,
                            ariaLabel: 'DRI Tools',
                            icon: 'Toolbox',
                            key: routeKeys.DRITools,
                        },
                        {
                            name: 'SRM Assistant',
                            url: routeKeys.AssistantChat,
                            ariaLabel: 'SRM Assistant',
                            icon: 'Robot',
                            key: routeKeys.AssistantChat,
                        },
                    ],
                }
            );
        }
        setLinks(newLinks);
    }, [authContext.isUserAuthorized, showAdmin, showKustoAccess]);

    const navLinkGroups: INavLinkGroup[] = [
        {
            links: links,
        },
    ];

    return (
        <div className="MobileNavPanel-root">
            <Panel
                isLightDismiss
                isOpen={props.isOpen}
                onDismiss={props.dismissPanel}
                closeButtonAriaLabel="Close"
                headerText="Navigation"
                type={PanelType.customNear}
            >
                <div>
                    <Nav
                        styles={navStyles}
                        ariaLabel="Navigation menu"
                        groups={navLinkGroups}
                        selectedKey={selectedRouteKey}
                        linkAs={(props) => {
                            if (props.link!.name === 'Separator') {
                                return (
                                    <hr
                                        role="none"
                                        aria-hidden="true"
                                        style={{
                                            color: 'gray',
                                            margin: '0 15px',
                                        }}
                                    />
                                );
                            }
                            return (
                                <Link
                                    className={props.className}
                                    style={{ boxSizing: 'border-box' }}
                                    to={props.href!}
                                >
                                    <span
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {!!props.iconProps && (
                                            <IconButton
                                                iconProps={props.iconProps}
                                                tabIndex={-1}
                                                aria-hidden="true"
                                                styles={{
                                                    root: {
                                                        pointerEvents: 'none',
                                                    },
                                                }}
                                            />
                                        )}
                                        {props.children}
                                    </span>
                                </Link>
                            );
                        }}
                    />
                </div>
            </Panel>
        </div>
    );
};
